import { useState } from 'react';
import { T } from '../lib/constants';

export default function LibraryItemCard({
  item,
  onDelete,
  showGroupPublicationToggle,
  onToggleGroupPublication,
  isAdmin,
  folders,
  onMoveToFolder,
  onSharePaper,
  onRename,
  showInlineMove = false,
}) {
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [renaming,       setRenaming]       = useState(false);
  const [renameValue,    setRenameValue]    = useState(item.title || '');

  const hasMenu = !showInlineMove && (onDelete || (folders?.length > 0 && onMoveToFolder) || onRename);

  const commitRename = () => {
    const next = renameValue.trim();
    if (next && next !== item.title) onRename?.(item, next);
    setRenaming(false);
  };

  return (
    <div style={{
      padding:'14px 16px', borderRadius:12,
      border:`1px solid ${T.bdr}`, background:T.w, marginBottom:8,
    }}>
      {item.is_group_publication && (
        <div style={{
          fontSize:10.5, fontWeight:700, color:T.am,
          background:T.am2, display:'inline-block',
          padding:'1px 8px', borderRadius:20, marginBottom:6,
        }}>
          🏆 Group publication
        </div>
      )}

      {renaming ? (
        <div style={{marginBottom:6}}>
          <input
            autoFocus
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter')  { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { setRenaming(false); setRenameValue(item.title || ''); }
            }}
            onBlur={commitRename}
            style={{width:'100%', fontSize:13.5, fontWeight:700, lineHeight:1.4,
              padding:'5px 8px', borderRadius:7, fontFamily:'inherit',
              border:`1.5px solid ${T.v}`, outline:'none', color:T.text}}
          />
          <div style={{fontSize:11, color:T.mu, marginTop:3}}>
            Enter to save · Esc to cancel
          </div>
        </div>
      ) : (
        <div style={{fontSize:13.5, fontWeight:700, lineHeight:1.4, marginBottom:4}}>
          {item.title}
        </div>
      )}

      {item.authors && (
        <div style={{fontSize:11.5, color:T.mu, marginBottom:4}}>
          {item.authors.slice(0,100)}{item.authors.length>100?'…':''}
        </div>
      )}

      <div style={{display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', marginBottom:8}}>
        {(item.citation || item.journal) && (
          <span style={{fontSize:12, color:T.mu}}>{item.citation || [item.journal, item.year].filter(Boolean).join(' · ')}</span>
        )}
        {item.cited_by_count > 0 && (
          <span style={{fontSize:10.5, background:T.bl2, color:T.bl,
            padding:'1px 7px', borderRadius:20, fontWeight:600}}>
            {item.cited_by_count} citations
          </span>
        )}
        {item.is_open_access && (
          <span style={{fontSize:10.5, background:T.gr2, color:T.gr,
            padding:'1px 7px', borderRadius:20, fontWeight:700}}>
            Open Access
          </span>
        )}
      </div>

      {item.notes && (
        <div style={{fontSize:12, color:T.mu, fontStyle:'italic',
          marginBottom:8, padding:'6px 10px', background:T.s2, borderRadius:7}}>
          "{item.notes}"
        </div>
      )}

      <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'center'}}>
        {onSharePaper && (item.doi || item.title) && (
          <button onClick={() => onSharePaper(item)} style={{
            fontSize:11.5, color:T.v, fontWeight:700, cursor:'pointer',
            border:`1.5px solid ${T.v}`, background:T.v2,
            padding:'3px 10px', borderRadius:20, fontFamily:'inherit',
          }}>
            Share this paper
          </button>
        )}
        {item.doi && (
          <a href={`https://doi.org/${item.doi}`} target="_blank" rel="noopener noreferrer"
            style={{fontSize:11.5, color:T.v, fontWeight:600, textDecoration:'none'}}>
            DOI ↗
          </a>
        )}
        {item.full_text_url && (
          <a href={item.full_text_url} target="_blank" rel="noopener noreferrer"
            style={{fontSize:11.5, color:T.gr, fontWeight:600, textDecoration:'none'}}>
            Full text ↗
          </a>
        )}
        {item.pdf_url && (
          <a href={item.pdf_url} target="_blank" rel="noopener noreferrer"
            style={{fontSize:11.5, color:T.bl, fontWeight:600, textDecoration:'none'}}>
            📄 PDF
          </a>
        )}

        {showGroupPublicationToggle && (
          <button onClick={() => onToggleGroupPublication(item)} style={{
            fontSize:11, fontWeight:600, cursor:'pointer',
            border:`1px solid ${item.is_group_publication ? T.am : T.bdr}`,
            background: item.is_group_publication ? T.am2 : T.w,
            color: item.is_group_publication ? T.am : T.mu,
            padding:'2px 9px', borderRadius:20, fontFamily:'inherit',
          }}>
            {item.is_group_publication ? '★ Our publication' : '☆ Mark as ours'}
          </button>
        )}

        {/* Unsorted view: inline move select + remove */}
        {showInlineMove && folders?.length > 0 && onMoveToFolder && (
          <select
            defaultValue=""
            onChange={e => { if (e.target.value) { onMoveToFolder(item, e.target.value); e.target.value = ''; } }}
            style={{
              fontSize:11.5, color:T.v, fontWeight:600,
              border:`1px solid ${T.v}`, borderRadius:20, background:T.v2,
              padding:'2px 8px', cursor:'pointer', fontFamily:'inherit',
            }}
          >
            <option value="">Move to folder…</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        {showInlineMove && onDelete && (
          <button onClick={() => onDelete(item)} style={{
            marginLeft:'auto', fontSize:11.5, color:T.ro,
            border:'none', background:'transparent',
            cursor:'pointer', fontFamily:'inherit',
          }}>
            Remove
          </button>
        )}

        {/* Regular folder view: 3-dot menu */}
        {hasMenu && (
          <div style={{marginLeft:'auto', position:'relative'}}>
            {menuOpen && (
              <div
                style={{position:'fixed', inset:0, zIndex:9}}
                onClick={() => setMenuOpen(false)}
              />
            )}
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(m => !m); setShowMovePicker(false); }}
              style={{
                fontSize:18, color:T.mu, lineHeight:1,
                border:'none', background:'transparent',
                cursor:'pointer', padding:'0 4px', letterSpacing:1,
              }}
              title="More options"
            >
              ···
            </button>
            {menuOpen && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position:'absolute', right:0, top:'calc(100% + 4px)',
                  background:T.w, border:`1px solid ${T.bdr}`,
                  borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,.12)',
                  zIndex:10, minWidth:160, overflow:'hidden',
                }}
              >
                {onRename && (
                  <button
                    onClick={() => { setRenameValue(item.title || ''); setRenaming(true); setMenuOpen(false); }}
                    style={{
                      display:'block', width:'100%', textAlign:'left',
                      padding:'9px 14px', fontSize:13, fontFamily:'inherit',
                      border:'none', background:'transparent', cursor:'pointer',
                      color:T.text,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.s2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Rename
                  </button>
                )}
                {folders?.length > 0 && onMoveToFolder && (
                  <button
                    onClick={() => { setShowMovePicker(true); setMenuOpen(false); }}
                    style={{
                      display:'block', width:'100%', textAlign:'left',
                      padding:'9px 14px', fontSize:13, fontFamily:'inherit',
                      border:'none', background:'transparent', cursor:'pointer',
                      color:T.text,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.s2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Move to folder
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => { onDelete(item); setMenuOpen(false); }}
                    style={{
                      display:'block', width:'100%', textAlign:'left',
                      padding:'9px 14px', fontSize:13, fontFamily:'inherit',
                      border:'none', background:'transparent', cursor:'pointer',
                      color:T.ro,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.ro2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Move picker — shown after clicking "Move to folder" from 3-dot menu */}
      {showMovePicker && (
        <div style={{marginTop:10, display:'flex', gap:8, alignItems:'center'}}>
          <select
            autoFocus
            defaultValue=""
            onChange={e => { if (e.target.value) { onMoveToFolder(item, e.target.value); setShowMovePicker(false); } }}
            style={{
              flex:1, padding:'6px 10px', borderRadius:8,
              border:`1.5px solid ${T.v}`, fontSize:13,
              fontFamily:'inherit', outline:'none', color:T.text, background:T.w,
            }}
          >
            <option value="">Select folder…</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <button
            onClick={() => setShowMovePicker(false)}
            style={{
              fontSize:12, color:T.mu, border:'none',
              background:'transparent', cursor:'pointer', fontFamily:'inherit',
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
